import React, { useState, useEffect, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { Card, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

// Admin triage for member-submitted venue suggestions (user_place_suggestions).
// Reads + writes go through /api/place-suggestions (service role) since the
// table's RLS scopes rows to their submitter. Approve geocodes via Nominatim and
// inserts the venue into Locations as 'sanoire-curated'.

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

function PlaceSuggestionsBody() {
  const { t } = useConsoleTheme();
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [approved, setApproved] = useState(0);

  const authHeader = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data && data.session ? data.session.access_token : null;
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setMsg(null);
    try {
      const headers = await authHeader();
      const r = await fetch('/api/place-suggestions?status=pending', { headers });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Failed to load');
      setRows(j.rows || []);
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const act = useCallback(async (id, action) => {
    let notes = null;
    if (action === 'reject') {
      notes = window.prompt('Reason for rejecting (optional):', '') || null;
    } else if (action === 'duplicate') {
      notes = window.prompt('Note (optional) — which venue is this a duplicate of?', '') || null;
    }
    setBusy(id); setMsg(null);
    try {
      const headers = { 'Content-Type': 'application/json', ...(await authHeader()) };
      const r = await fetch('/api/place-suggestions', {
        method: 'POST', headers, body: JSON.stringify({ id, action, notes }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Action failed');
      setRows(prev => prev.filter(x => x.id !== id));
      if (action === 'approve') {
        setApproved(n => n + 1);
        setMsg({ type: 'ok', text: `Approved — added to Locations (#${j.location_id}) at ${j.lat}, ${j.lng}.` });
      } else {
        setMsg({ type: 'ok', text: action === 'duplicate' ? 'Marked as duplicate.' : 'Rejected.' });
      }
    } catch (e) {
      setMsg({ type: 'error', text: e.message });
    } finally {
      setBusy(null);
    }
  }, [authHeader]);

  return (
    <div style={{ fontFamily: F, padding: isMobile ? '16px 14px 80px' : '24px 28px' }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 500, margin: 0, color: 'var(--color-text-primary)' }}>Place suggestions</h2>
        <p style={{ fontSize: '.78rem', color: 'var(--color-text-secondary)', margin: '4px 0 0' }}>
          Venues members nominated via the app. Approve to geocode and add to the directory as a curated record, or reject / mark duplicate.
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px,1fr))', gap: 10, marginBottom: 20 }}>
        {[
          { label: 'Pending', val: rows.length.toString(), color: '#fd7e14' },
          { label: 'Approved this session', val: approved.toString(), color: '#28a745' },
        ].map(({ label, val, color }) => (
          <Card key={label} style={{ padding: '12px 14px' }}>
            <div style={{ fontSize: '.65rem', color: 'var(--color-text-secondary)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 500, color }}>{val}</div>
          </Card>
        ))}
      </div>

      {/* Reload */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={load} disabled={loading}
          style={{ padding: '7px 18px', borderRadius: 8, background: t.accent, color: '#AFE1F6', border: 'none', fontSize: '.78rem', fontWeight: 500, cursor: loading ? 'default' : 'pointer', fontFamily: F }}>
          {loading ? 'Loading…' : 'Reload'}
        </button>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Insight type={msg.type === 'error' ? 'warn' : 'ok'}>{msg.text}</Insight></div>}

      {rows.length === 0 && !loading && (
        <Insight type="info">No pending suggestions. When members nominate a place, it lands here.</Insight>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map(row => (
          <Card key={row.id} style={{ padding: '14px 16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '.85rem', fontWeight: 500, color: 'var(--color-text-primary)', marginBottom: 3 }}>{row.name}</div>
                <div style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)' }}>
                  {[row.type, row.city, row.country].filter(Boolean).join(' · ')}
                </div>
                {row.address && <div style={{ fontSize: '.7rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>{row.address}</div>}
                {row.website_or_phone && <div style={{ fontSize: '.7rem', color: '#6F8FA7', marginTop: 2 }}>{row.website_or_phone}</div>}
                {row.why && <div style={{ fontSize: '.72rem', color: 'var(--color-text-secondary)', marginTop: 5, fontStyle: 'italic' }}>“{row.why}”</div>}
                <div style={{ fontSize: '.62rem', color: 'var(--color-text-secondary)', marginTop: 5 }}>
                  {row.created_at ? new Date(row.created_at).toLocaleDateString() : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button onClick={() => act(row.id, 'approve')} disabled={busy === row.id}
                  style={{ padding: '5px 12px', borderRadius: 6, background: '#28a745', color: '#fff', border: 'none', fontSize: '.72rem', cursor: busy === row.id ? 'default' : 'pointer', fontFamily: F, fontWeight: 500 }}>
                  {busy === row.id ? '…' : '✓ Approve'}
                </button>
                <button onClick={() => act(row.id, 'duplicate')} disabled={busy === row.id}
                  style={{ padding: '5px 12px', borderRadius: 6, background: 'none', color: '#9F7E4F', border: '1px solid #9F7E4F', fontSize: '.72rem', cursor: busy === row.id ? 'default' : 'pointer', fontFamily: F }}>
                  Duplicate
                </button>
                <button onClick={() => act(row.id, 'reject')} disabled={busy === row.id}
                  style={{ padding: '5px 12px', borderRadius: 6, background: 'none', color: '#dc3545', border: '1px solid #dc3545', fontSize: '.72rem', cursor: busy === row.id ? 'default' : 'pointer', fontFamily: F }}>
                  Reject
                </button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function PlaceSuggestions(props) {
  if (props && props.embed) return <PlaceSuggestionsBody {...props} />;
  return <ConsoleShell title="Place Suggestions"><PlaceSuggestionsBody {...props} /></ConsoleShell>;
}
